import { useState, ReactNode, useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { useAppDispatch, useAppSelector } from "../../stores/hooks/hooks.ts";
import { useDocumentTitle } from "../../helpers/hooks/useDocumentTitle.tsx";
import { useScrollPosition } from "../../helpers/hooks/useScrollPosition.ts";
import {
  RevAlert,
  RevAlertListShell,
  RevBreadcrumbs,
  type BreadcrumbItem,
  RevMainTemplate,
} from "@revmatics/catalyst";

import { SideBar } from "./components/sidebar/SideBar.tsx";
import { Header } from "./components/header/Header.tsx";

import { fetchProfileData } from "../../stores/thunks/profileThunk.tsx";
import { fetchBrandList } from "../../stores/thunks/brandThunk.tsx";
import { closeAlert, closeAllAlert } from "../../stores/slice/alertSlice.tsx";
import BackgroundImage from "/images/hero.png";
import { GlobalSlideouts } from "./components/globalSlideouts/GlobalSlideouts.tsx";
import { routeStorageRules } from "./layoutData.ts";
import { fetchOrganizationsList } from "../../stores/thunks/organizationsThunk.tsx";
import { fetchCompanySettings } from "../../stores/thunks/companyThunk.tsx";
import { setCurrentOrganization } from "../../stores/slice/organizationsSlice.tsx";

declare global {
  interface Window {
    Appcues?: {
      page: () => void;
      identify: (
        id: string | number,
        properties?: Record<string, string | number | boolean | Date | null>,
      ) => void;
    };
  }
}

type Props = {
  children: ReactNode;
  container?: boolean;
  title?: string;
  isAuthLayout?: boolean;
  isOnboardingLayout?: boolean;
  initSideBarOpen?: boolean;
  crumbs?: BreadcrumbItem[];
  headerStick?: boolean;
  headerSize?: "regular" | "large";
  tableExpand?: boolean;
};

export const RevLayout = ({
  children,
  container = false,
  title = "Revmatics",
  isAuthLayout = false,
  isOnboardingLayout = false,
  initSideBarOpen = true,
  crumbs,
  headerStick = false,
  headerSize = "regular",
  tableExpand = false,
}: Props) => {
  const [sideBarOpen, setSideBarOpen] = useState(initSideBarOpen);
  const dispatch = useAppDispatch();

  const scrollContainerRef = useRef<HTMLDivElement>(
    null,
  ) as React.RefObject<HTMLDivElement>;

  const { yPos } = useScrollPosition(scrollContainerRef);
  const isSticky: boolean = yPos > 64;
  useDocumentTitle({ title });
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  const brandList = useAppSelector((state) => state.brand.brandList);
  const profile = useAppSelector((state) => state.profile.profile);
  const { organizationsList, currentOrganization } = useAppSelector(
    (state) => state.organizations,
  );
  const { companySettings } = useAppSelector((state) => state.company);
  const alertProps = useAppSelector((state) => state.alert.alertProps);
  const alertDuration = isAuthLayout ? 10000 : 5000;

  useEffect(() => {
    if (window.Appcues) {
      window.Appcues.page();
    }
  }, [location.pathname]);

  useEffect(() => {
    const prevPath = prevPathRef.current;
    const currentPath = location.pathname;

    routeStorageRules.forEach(({ key, isInScope }) => {
      const wasInScope = isInScope(prevPath);
      const isInScopeNow = isInScope(currentPath);

      if (wasInScope && !isInScopeNow) {
        sessionStorage.removeItem(key);
      }
    });

    prevPathRef.current = currentPath;
  }, [location.pathname]);

  useEffect(() => {
    const timeoutId = setTimeout(
      () => dispatch(closeAllAlert()),
      alertDuration,
    );
    return () => clearTimeout(timeoutId);
  }, [dispatch, alertProps.length, alertDuration]);

  useEffect(() => {
    if (!brandList && !isAuthLayout) dispatch(fetchBrandList());
    if (!profile && !isAuthLayout) dispatch(fetchProfileData());
    if (!organizationsList && !isAuthLayout) dispatch(fetchOrganizationsList());
    if (!companySettings && !isAuthLayout) dispatch(fetchCompanySettings());
  }, [
    brandList,
    profile,
    isAuthLayout,
    dispatch,
    organizationsList,
    companySettings,
  ]);

  useEffect(() => {
    if (companySettings && organizationsList && !currentOrganization) {
      const currentCompanyId = organizationsList.find(
        (item) => item.id === companySettings.id,
      )?.keycloak_organization_id;
      dispatch(setCurrentOrganization(currentCompanyId ?? null));
    }
  }, [dispatch, organizationsList, currentOrganization, companySettings]);

  const pageAlerts = (
    <RevAlertListShell>
      {alertProps.map((alert) => (
        <RevAlert
          key={alert.id}
          data={alert}
          handleClose={() => dispatch(closeAlert(alert.id))}
        />
      ))}
    </RevAlertListShell>
  );

  const breadcrumbSlot = crumbs ? (
    <RevBreadcrumbs
      crumbs={crumbs}
      crumbsClassName={headerStick ? `mb-4` : `mb-6`}
    />
  ) : undefined;

  // --- Auth Layout Mapping ---
  if (isAuthLayout) {
    return (
      <RevMainTemplate
        hasContainer={false}
        fullWidth={true}
        alerts={pageAlerts}
        isSidebarOpen={false}
      >
        <div className="flex min-h-dvh">
          <div className="w-full lg:w-1/2 bg-primary-25 flex flex-col px-8 sm:px-12 pt-8 sm:pt-12 pb-2 order-2 lg:order-1">
            <div className="flex-grow flex flex-col justify-center items-center w-full">
              <div className="w-full max-w-[439px]">{children}</div>
            </div>
            <p className="text-center text-xs text-gray-500 pt-8">
              {new Date().getFullYear()} &copy; Revmatics AI
            </p>
          </div>
          <div
            className="hidden lg:flex lg:w-1/2 bg-cover bg-center flex-col justify-center items-center text-white p-8 order-1 lg:order-2 relative"
            style={{ backgroundImage: `url(${BackgroundImage})` }}
          >
            <div className="absolute inset-0 bg-black opacity-40"></div>
            <div className="relative z-10 text-center max-w-xl">
              <h1 className="font-display font-extralight text-[50px] tracking-[1.5px] mb-6">
                Connecting the Stars.
              </h1>
            </div>
          </div>
        </div>
      </RevMainTemplate>
    );
  }

  // --- Standard / Onboarding Mapping ---
  return (
    <RevMainTemplate
      hasContainer={container && !isOnboardingLayout}
      /* If onboarding, we trigger the p-0 padding and max-w-none constraints */
      fullWidth={isOnboardingLayout}
      isSidebarOpen={sideBarOpen}
      isSticky={isSticky}
      headerStick={headerStick}
      headerSize={headerSize}
      tableExpand={tableExpand}
      scrollRef={scrollContainerRef}
      alerts={pageAlerts}
      breadcrumbs={breadcrumbSlot}
      header={
        !isOnboardingLayout ? (
          <Header
            openSideBar={sideBarOpen}
            handleClick={() => setSideBarOpen(!sideBarOpen)}
          />
        ) : undefined
      }
      sidebar={
        !isOnboardingLayout ? (
          <SideBar
            open={sideBarOpen}
            handleClose={() => setSideBarOpen(!sideBarOpen)}
          />
        ) : undefined
      }
    >
      {children}
      <GlobalSlideouts />
    </RevMainTemplate>
  );
};
